/**
 * Converts an API duration string (HH:MM:SS) into whole days for the form.
 *
 * @param {string|number|null|undefined} timeStr
 * @return {number}
 */
export const timeInDays = timeStr => {
  if (timeStr === null || timeStr === undefined || timeStr === "") {
    return 0;
  }
  if (typeof timeStr === "number" && Number.isFinite(timeStr)) {
    return Math.max(0, Math.round(timeStr / 86400));
  }
  const parts = String(timeStr).split(":").map(Number);
  const hours = Number.isFinite(parts[0]) ? parts[0] : 0;
  return Math.max(0, parseInt(hours / 24, 10) || 0);
};

/**
 * Extracts facility type keys from API ``types`` (ChoicePairs) or legacy ``type``.
 *
 * @param {object} warehouse
 * @return {string[]}
 */
export const extractWarehouseTypeIds = warehouse => {
  const { types, type } = warehouse || {};

  if (Array.isArray(types) && types.length) {
    return types.map(item => (item && typeof item === "object" ? item.id : item)).filter(Boolean);
  }

  // Legacy scalar ChoicePair / string (pre-migration cache / old payloads).
  if (type && typeof type === "object" && type.id) {
    return [type.id];
  }
  if (typeof type === "string" && type.trim()) {
    return [type.trim()];
  }

  return [];
};

export const buildWarehouseSubmitPayload = values => {
  const { type_ids, site_id, partner_id, ...rest } = values;

  const normalizedTypeIds = Array.isArray(type_ids)
    ? type_ids.filter(Boolean)
    : type_ids
      ? [type_ids]
      : [];

  const is3plOr3pm = normalizedTypeIds.includes("3pl") || normalizedTypeIds.includes("3pm");

  return {
    name: rest.name,
    code: rest.code,
    short_description: rest.short_description || "",
    long_description: rest.long_description || "",
    type_ids: normalizedTypeIds,
    site_id: site_id || null,
    partner_id: is3plOr3pm ? partner_id || null : null,
    local_transit_time_buffer_air: Number(rest.local_transit_time_buffer_air || 0) * 86400,
    local_transit_time_buffer_ocean: Number(rest.local_transit_time_buffer_ocean || 0) * 86400,
  };
};

export const buildInitialValues = warehouse => {
  const data = warehouse && typeof warehouse === "object" ? warehouse : {};
  const {
    name,
    short_description,
    long_description,
    code,
    site,
    partner,
    local_transit_time_buffer_air,
    local_transit_time_buffer_ocean,
  } = data;

  const typeIds = extractWarehouseTypeIds(data);

  return {
    name: name || "",
    short_description: short_description || "",
    long_description: long_description || "",
    local_transit_time_buffer_air: timeInDays(local_transit_time_buffer_air || "00:00:00"),
    local_transit_time_buffer_ocean: timeInDays(local_transit_time_buffer_ocean || "00:00:00"),
    code: code || "",
    type_ids: typeIds.length ? typeIds : ["warehouse"],
    site_id: site?.id ?? null,
    partner_id: partner?.id ?? null,
  };
};
